import {
  SnackbarMessage,
  VariantType,
  closeSnackbar,
  enqueueSnackbar,
} from "notistack";
import { HTMLAttributes } from "react";

import Zoom from "@mui/material/Zoom";

import getCloseIcon from "veneer/SnackBar/CloseIcon";

interface VeneerSnackbarProps extends HTMLAttributes<HTMLDivElement> {
  "data-testid"?: string;
  "data-component"?: string;
}

export interface SnackbarOptions {
  allowDuplicates?: boolean;
}

interface SnackBarProps extends SnackbarOptions {
  autoHideDuration?: number;
  dataTestId?: string;
  message: SnackbarMessage;
  variant: VariantType;
}

const showSnackBar = ({
  allowDuplicates = false,
  autoHideDuration,
  dataTestId,
  message,
  variant,
}: SnackBarProps): Promise<void> => {
  return new Promise<void>((resolve) => {
    enqueueSnackbar(message, {
      action: getCloseIcon,
      anchorOrigin: { vertical: "top", horizontal: "center" },
      autoHideDuration: autoHideDuration ?? null,
      onClose: () => resolve(),
      preventDuplicate: !allowDuplicates,
      SnackbarProps: {
        "data-component": `veneer-snackbar-${variant}`,
        "data-testid": dataTestId,
      } as VeneerSnackbarProps,
      TransitionComponent: Zoom,
      variant: variant,
    });
  });
};

export const showSuccess = (
  message: SnackbarMessage,
  dataTestId?: string,
  options?: SnackbarOptions,
): Promise<void> =>
  showSnackBar({
    message,
    variant: "success",
    autoHideDuration: 5000,
    dataTestId,
    ...options,
  });

export const showInfo = (
  message: SnackbarMessage,
  dataTestId?: string,
  options?: SnackbarOptions,
): Promise<void> =>
  showSnackBar({
    message,
    variant: "info",
    dataTestId,
    ...options,
  });

export const showWarning = (
  message: SnackbarMessage,
  dataTestId?: string,
  options?: SnackbarOptions,
): Promise<void> =>
  showSnackBar({
    message,
    variant: "warning",
    dataTestId,
    ...options,
  });

export const showError = (
  message: SnackbarMessage,
  dataTestId?: string,
  options?: SnackbarOptions,
): Promise<void> =>
  showSnackBar({
    message,
    variant: "error",
    dataTestId,
    ...options,
  });

export const closeAll = () => closeSnackbar();

export const MAX_SNACKBARS = 20;
